import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { propTypes } from '../../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';
import {
  facebookIcon,
  instagramIcon,
  linkedinIcon,
  pinterestIcon,
  tiktokIcon,
  xIcon,
  youtubeIcon,
} from '../../../PageBuilder/Primitives/Link/Icons';
import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import MenuIcon from '../MenuIcon';
import searchIcon from '../../../../assets/search.svg';
import { SocialMediaLink } from '../../../PageBuilder/Primitives/Link';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import IconLogo from '../../../../components/IconLogo/IconLogo';
import e from 'cors';
import SocialLinks from './CustomLinksMenu/SocialLinks';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, currentUserHasListings }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="ManageListingsPage">
          {currentUser?.attributes?.profile?.publicData?.userType === 'buyer' ? (
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('FavListingsPage'))}
              name="FavListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="UserNav.favListings" />
            </NamedLink>
          ) : (
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          )}
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>

        <MenuItem key="InboxPage">
          <NamedLink
            className={classNames(css.menuLink)}
            name="InboxPage"
            params={{ tab: 'orders' }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.chat" />
          </NamedLink>
        </MenuItem>

        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
  } = props;
  const [mounted, setMounted] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const openNavbar = e => {
    e.preventDefault();
    setOpen(true);
  };
  const closeNavbar = e => {
    setOpen(false);
  };
  useEffect(() => {
    setMounted(true);
  }, []);

  const history = useHistory();

  const location = useLocation();

  const [localParams, setLocalParams] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('local');
  });

  // default language
  const getUserCountry = async () => {
    const ipData = await fetch(`https://ipinfo.io/?token=f659e9f7cf8786`);
    const response = await ipData.json();
    return response.country;
  };

  const changeDefaultLanguage = async () => {
    const country = await getUserCountry();
    if (country === 'DE' || country === 'AT' || country === 'CH') {
      let toSearch = location.search;
      if (toSearch?.includes('local=')) {
        toSearch = toSearch.replace(/local=[^&]*/, `local=de`);
      } else {
        if (!toSearch) {
          toSearch = `?local=de`;
        } else {
          toSearch += `&local=de`;
        }
      }
      setLocalParams('de');
      history.push(`${location.pathname}${toSearch}`);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get('local')) {
      changeDefaultLanguage();
    }
  }, []);
  const changeLanguage = e => {
    const searchValue = e?.target?.value;
    setLocalParams(searchValue);
    const params = new URLSearchParams(location.search);
    params.set('local', searchValue);
    history.push(`${location.pathname}?${params.toString()}`);
    window.location.reload();
  };

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu currentPage={currentPage} currentUser={currentUser} onLogout={onLogout} />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  return (
    <>
      <nav className={classes}>
        <div className={css.toogleButton}>
          <button
            onClick={e => {
              openNavbar(e);
            }}
          >
            <span className={css.toggleLine}></span>
            <span className={css.toggleLine}></span>
            <span className={css.toggleLine}></span>
          </button>
        </div>
        {/* <MenuIcon className={css.menuIcon} /> */}
        {/* <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      /> */}
        <NamedLink className={css.logoLink} name="LandingPage">
          <IconLogo />
        </NamedLink>

        {/* <TopbarSearchForm
        className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
        desktopInputRoot={css.topbarSearchWithLeftPadding}
        onSubmit={onSearchSubmit}
        initialValues={initialSearchFormValues}
        appConfig={config}
      /> */}

        {/* <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
      /> */}

        {/* {inboxLinkMaybe} */}
        <div className={css.lang}>
          <select
            value={localParams ? localParams : 'en'}
            name="language"
            onChange={changeLanguage}
          >
            <option value={'en'}>EN</option>
            <option value={'de'}>DE</option>
          </select>
        </div>
        <div className={css.profileUser}>
          {authenticatedOnClientSide && (
            <div className={css.profile}>
              <p>
                {currentUser?.attributes?.profile?.firstName}{' '}
                {currentUser?.attributes?.profile?.lastName}
              </p>
              {profileMenuMaybe}
            </div>
          )}
        </div>

        {/* {signupLinkMaybe} */}
        <div className="LoginButton">{loginLinkMaybe}</div>
      </nav>
      {isOpen && (
        <div className={css.side_nav}>
          <div className={css.innerBlockSide}>
            <div className={css.sideBarLogo}>
              {/* <LinkedLogo
                className={css.logoLink}
                layout="desktop"
                alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
                linkToExternalSite={config?.topbar?.logoLink}
              /> */}

              <NamedLink name="LandingPage">
                <IconLogo />
              </NamedLink>
              <button onClick={closeNavbar}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="34"
                  height="34"
                  viewBox="0 0 34 34"
                  fill="none"
                >
                  <path d="M2 2L32 32M32 2L2 32" stroke="#A645D6" stroke-width="4" />
                </svg>
              </button>
            </div>
            <div className={css.url_link}>
              <button
                className="buy_button"
                onClick={() => {
                  history.push('/s');
                  window.location.reload();
                }}
              >
                {intl.formatMessage({ id: 'Topbar.ModelOpen.buyValue' })}
              </button>
              <button
                Name="buy_button"
                onClick={() => {
                  history.push('/listings');
                  window.location.reload();

                }}
              >
                {intl.formatMessage({ id: 'Topbar.ModelOpen.sellValue' })}
              </button>
              <button
                Name="buy_button"
                onClick={() => {
                  history.push('/learn');
                  window.location.reload();

                }}
              >
                {intl.formatMessage({ id: 'Topbar.ModelOpen.learnValue' })}
              </button>
              {/* <div className={css.searchBar}>
                <input
                  type="text"
                  name="search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                <svg
                  classNames={css.searhIcon}
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M17.1901 15.4782L23.6458 21.934C23.8727 22.1611 24.0001 22.469 24 22.79C23.9999 23.111 23.8723 23.4189 23.6452 23.6458C23.4181 23.8727 23.1102 24.0001 22.7892 24C22.4682 23.9999 22.1604 23.8723 21.9335 23.6452L15.4778 17.1893C13.5479 18.6841 11.1211 19.3875 8.69104 19.1565C6.26097 18.9255 4.01018 17.7773 2.39656 15.9457C0.782936 14.114 -0.0723147 11.7364 0.00479537 9.29655C0.0819054 6.85668 1.08558 4.53783 2.81165 2.81172C4.53771 1.08561 6.85651 0.0819075 9.29632 0.00479549C11.7361 -0.0723165 14.1137 0.782955 15.9453 2.39662C17.7769 4.01028 18.925 6.26113 19.156 8.69126C19.387 11.1214 18.6836 13.5483 17.1889 15.4782H17.1901ZM9.60045 16.7993C11.5099 16.7993 13.3412 16.0408 14.6914 14.6905C16.0416 13.3403 16.8001 11.509 16.8001 9.59949C16.8001 7.68997 16.0416 5.85866 14.6914 4.50843C13.3412 3.15819 11.5099 2.39964 9.60045 2.39964C7.69098 2.39964 5.85971 3.15819 4.50951 4.50843C3.15932 5.85866 2.40078 7.68997 2.40078 9.59949C2.40078 11.509 3.15932 13.3403 4.50951 14.6905C5.85971 16.0408 7.69098 16.7993 9.60045 16.7993Z"
                    fill="#0EC3A8"
                  />
                </svg>
              </div> */}
            </div>
            <div className={css.site_link}>
              <SocialLinks />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  config: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  config: object,
};

export default TopbarDesktop;
